import React, { useState, useEffect, useRef } from "react";
import { useRouter } from "next/router";
import translate from "@deosite/helpers/langLibrary";
import { default as themeDajar } from "../../../theme";

import Category from "../../c/category";

import Box from "@commerce-ui/core2/Box";
import useWindowSize from "@commerce-ui/core2/useWindowSize";

import Pagination from "../../../components/Pagination";
import FiltersBarDesktop from "../../../components/FiltersBarDesktop";
import FiltersBarMobile from "../../../components/FiltersBarMobile";
import MetaTags from "../../../components/MetaTags";

import {
  useSearchNew,
  fetcher as searchFetcher
} from "@deosite/api/hooks/REST/useSearchNew";

import useAnalyticsAndCommerceTags from "@deosite/api/hooks/common/useAnalytics4AndCommerceTags";
import useCommitedFilters from "@deosite/api/hooks/common/useNormalizeAndMapFilters";

import oneProductResultWithoutFilters from "@deosite/helpers/oneProductResultWithoutFilters";
import searchInitialProps from "@deosite/helpers/searchInitialProps";

function SearchPage(props) {
  const {
    initialProductsAndFilters,
    initialQuery,
    pickedFilters,
    cache
  } = props;

  const router = useRouter();

  const query = initialQuery || router.query.query;

  const translations = {
    searchResults: translate("Search results | Dajar"),
    searchResultsWithQuery: translate("search results", { phrase: query })
  };

  const initialPage = router.asPath.match(/p=([0-9]*)/)?.[1] ?? 1;

  const size = useWindowSize();
  const [isMobile, setMobile] = useState(null);

  const firstUpdate = useRef({ query: "", isFirst: true });

  useEffect(() => {
    const isMobileSize = size.width <= parseInt(themeDajar().breakpoints.md);
    if (isMobile !== isMobileSize) {
      setMobile(isMobileSize);
    }
  }, [size.width]);

  useEffect(() => {
    if (firstUpdate.current.isFirst) {
      firstUpdate.current.isFirst = false;
    }
  });

  const {
    results: products,
    filters,
    totalCount,
    changeSearchPagination
  } = useSearchNew({
    phrase: query,
    currentPage: initialPage,
    pageSize: global.__appConfig__.categoryPageSizeLargerRange,
    initialData: initialProductsAndFilters,
    pickedFilters,
    cacheSSR: cache,
    router
  });

  const { filters: filtersData } = useCommitedFilters({
    data: filters
  });

  const { searchPhrase } = useAnalyticsAndCommerceTags();

  useEffect(() => {
    searchPhrase(query[0]);
  }, [query]);

  if (oneProductResultWithoutFilters({ totalCount, products, filtersData })) {
    router?.push(`/[...categoryOrProductPage]`, `/${products[0].url}`, {
      shallow: true
    });
  }

  const changeUrlAfterFiltering = async filters => {
    const url = await changeSearchPagination(
      filters,
      1,
      global.__appConfig__.categoryPageSizeLargerRange,
      query
    );

    router.push(`/catalogsearch/result/[...query]`, url, { shallow: true });
  };

  const filtersBar = pickedFilters && isMobile !== null && (
    <>
      {isMobile ? (
        <FiltersBarMobile
          data={filtersData}
          onChange={changeUrlAfterFiltering}
        />
      ) : (
        <FiltersBarDesktop
          data={filtersData}
          onChange={changeUrlAfterFiltering}
        />
      )}
    </>
  );

  const onPageChange = async pageToSet => {
    const asPath = router.asPath;
    const urlPath =
      asPath.indexOf("?p=") >= 0 ? asPath.split("?p=") : asPath.split("&p=");

    await changeSearchPagination(
      filtersData,
      pageToSet,
      global.__appConfig__.categoryPageSizeLargerRange,
      query
    );

    const pathAlreadyContainsQuestionMark = urlPath[0]?.indexOf("?") >= 0;

    const pageStringToSet = pathAlreadyContainsQuestionMark
      ? `&p=${pageToSet}`
      : `?p=${pageToSet}`;

    router
      .push(
        `/catalogsearch/result/[...query]`,
        `${urlPath[0]}${pageStringToSet}`,
        {
          shallow: true
        }
      )
      .then(() => window.scrollTo(0, 0));
  };

  const pagesAmount = Math.ceil(
    totalCount / global.__appConfig__.categoryPageSizeLargerRange
  );

  const pagination = (
    <Pagination
      pagesAmount={Number.isNaN(pagesAmount) ? 1 : pagesAmount}
      currentPage={initialPage}
      onChange={onPageChange}
    />
  );

  return (
    <>
      <MetaTags
        title={translations.searchResults}
        description={translations.searchResults}
        noFollow={true}
      />

      <Box sx={{ mt: "s9", mb: "s9" }} />
      <Category
        categoryProducts={products ?? []}
        totalCount={totalCount}
        currentCategory={{
          name: translate("search results", { phrase: query })
        }}
        filtersBar={filtersBar}
        pagination={pagination}
        footerContent={props.footerContent}
        isSearchPage={true}
      />
    </>
  );
}

SearchPage.getInitialProps = async context =>
  await searchInitialProps({ context, searchFetcher });

export default SearchPage;
